import { CommonModule } from '@angular/common';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';

import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

import { FiltersAndPaginationPayloadService } from '../../@core/services/filters-and-pagination-payload.service';
import { QuotesActions } from '../../@store/quotes/quotes.actions';
import {
  selectArchivedQuoteSuccess,
  selectCustomers,
  selectStatuses,
  selectUnarchivedQuoteSuccess,
} from '../../@store/quotes/quotes.selectors';
import { selectAssignees } from '../../@store/quotes/quotes.selectors';
import { Customer } from '../../@store/quotes/quotes.state.interface';
import { SafeAny } from '../../types/safe-any';

@Component({
  standalone: true,
  selector: 'ciwc-quotes',
  templateUrl: './quotes.component.html',
  styleUrls: ['./quotes.component.scss'],
  imports: [
    CommonModule,
    MatCardModule,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatButtonModule,
    AsyncPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuotesComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly payloadService = inject(FiltersAndPaginationPayloadService);
  readonly assignees = this.store.selectSignal(selectAssignees);
  readonly customers = this.store.selectSignal(selectCustomers);
  readonly statuses = this.store.selectSignal(selectStatuses);
  readonly archivedQuoteSuccess = this.store.selectSignal(selectArchivedQuoteSuccess);
  readonly unarchivedQuoteSuccess = this.store.selectSignal(selectUnarchivedQuoteSuccess);

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<SafeAny>;

  currentTab = 0;
  searchControl = new FormControl('');

  // Filter controls
  clientFilterControl = new FormControl('');
  statusFilterControl = new FormControl('');
  assigneeFilterControl = new FormControl('');

  filteredOptions$: Observable<SafeAny> | undefined;

  constructor() {
    effect(() => {
      // todo - temporary console logs
      console.log('Quote archived successfully', this.archivedQuoteSuccess());
      console.log('Quote unarchived successfully', this.unarchivedQuoteSuccess());

      if (this.archivedQuoteSuccess() || this.unarchivedQuoteSuccess()) {
        this.initCurrentTab();
      }
    });
  }

  ngOnInit(): void {
    this.loadData();
    this.initCurrentTab();
    this.listenToFormChanges();
    this.listenToQuotesSearchChanges();
  }

  ngOnDestroy(): void {
    this.resetFormControls();
  }

  displayFn = (customer: Customer | string | null): string => {
    if (!customer) return '';
    if (typeof customer === 'string') return customer;

    return `${customer.firstName} ${customer.lastName}`;
  };

  onTabChange(index: number): void {
    this.currentTab = index;
    const route = index === 0 ? 'active-quotes' : 'archived-quotes';

    this.router.navigate(['/quotes', route]);
  }

  onClearFilters(): void {
    this.resetFormControls();
    this.dispatchQuotesType(this.currentTab, this.payloadService.combinedPayload);
  }

  private resetFormControls(): void {
    this.clientFilterControl.reset();
    this.statusFilterControl.reset();
    this.assigneeFilterControl.reset();

    this.payloadService.resetAll();
  }

  private loadData(): void {
    this.store.dispatch(QuotesActions.loadAssignees({ payload: { category: 'QUOTES' } }));
    this.store.dispatch(QuotesActions.loadStatuses({ payload: { identifier: 'QUOTES_STATUS' } }));
    this.store.dispatch(QuotesActions.loadCustomers());
  }

  private listenToFormChanges(): void {
    // Setup search control subscription
    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        this.payloadService.resetPagination();
        this.payloadService.updateQuotesSearchPayload(value || '');
        this.payloadService.updateFilterPayload({ q: value || '' });
        this.dispatchQuotesType(this.currentTab, this.payloadService.combinedPayload);
      });

    // Setup filtered options for searchable select
    this.filteredOptions$ = this.clientFilterControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(''),
      map((value) => {
        if (!value || typeof value !== 'string') {
          return this.customers();
        }
        const filterValue = value.toLowerCase();

        return this.customers()?.filter((customer: Customer) => {
          const fullName = `${customer.firstName} ${customer.lastName}`.toLowerCase();

          return fullName.includes(filterValue);
        });
      }),
    );

    // Handle client filter
    this.clientFilterControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(null),
        filter((value: SafeAny): value is SafeAny => {
          if (!value) return false;

          return typeof value === 'object' && 'id' in value;
        }),
      )
      .subscribe((client) => {
        if (!client) return;

        this.payloadService.resetPagination();

        this.payloadService.updateFilterPayload({
          clientId: client.id,
          archived: !!this.currentTab,
        });
        this.dispatchQuotesType(this.currentTab, this.payloadService.combinedPayload);
      });

    // Handle status filter
    this.statusFilterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), startWith(null), distinctUntilChanged())
      .subscribe((status) => {
        if (!status) return;

        this.payloadService.resetPagination();
        this.payloadService.updateFilterPayload({ status });
        this.dispatchQuotesType(this.currentTab, this.payloadService.combinedPayload);
      });

    // Handle assignee filter
    this.assigneeFilterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), startWith(null), distinctUntilChanged())
      .subscribe((assigneeId) => {
        if (!assigneeId) return;

        this.payloadService.resetPagination();
        this.payloadService.updateFilterPayload({ assigneeId });
        this.dispatchQuotesType(this.currentTab, this.payloadService.combinedPayload);
      });
  }

  private dispatchQuotesType(currentTab: number, payload: SafeAny): void {
    if (currentTab === 0) {
      this.store.dispatch(
        QuotesActions.loadActiveQuotes({
          payload: {
            ...payload,
            archived: false,
            sort: this.payloadService.currentSort || 'dateCreated',
            q: this.payloadService.currentQuotesSearchPayload,
          },
        }),
      );
    } else {
      this.store.dispatch(
        QuotesActions.loadArchivedQuotes({
          payload: {
            ...payload,
            archived: true,
            sort: this.payloadService.currentSort || 'dateCreated',
            q: this.payloadService.currentQuotesSearchPayload,
          },
        }),
      );
    }
  }

  private initCurrentTab(): void {
    this.currentTab = this.router.url.includes('active-quotes') ? 0 : 1;

    this.payloadService.updateFilterPayload({ archived: this.currentTab === 1 });
  }

  private listenToQuotesSearchChanges(): void {
    const currentSearchValue = this.payloadService.currentQuotesSearchPayload;

    if (currentSearchValue) {
      this.searchControl.setValue(currentSearchValue);
    }
  }
}
